import type { AxiosRequestConfig } from 'axios';
import type { ServiceRequestType } from '~/common/types/api.type';

import { useMemo } from 'react';

/**
 * Generic hook for creating service requests
 * Consolidates the repetitive request logic across all services
 */
export const useServiceRequest = (
  endpoint: string,
  method: AxiosRequestConfig['method'] = 'GET',
  params?: {},
): ServiceRequestType => {
  /** method */
  const httpMethod = useMemo(() => method, [method]);

  /** url */
  const url = useMemo(() => endpoint, [endpoint]);

  /** key */
  const key = useMemo<string | null>(() => {
    if (params) {
      return `[${httpMethod}] ${url} ${JSON.stringify(params)}`;
    }
    return `[${httpMethod}] ${url}`;
  }, [httpMethod, url, params]);

  /** config */
  const config = useMemo<AxiosRequestConfig>(() => {
    const baseConfig: AxiosRequestConfig = {
      url,
      method: httpMethod,
    };

    if (method === 'GET' && params) {
      // Handle array values in params
      const formattedParams = Object.entries(params).reduce(
        (acc, [key, value]) => {
          acc[key] = Array.isArray(value) ? value.join(',') : value;
          return acc;
        },
        {} as Record<string, unknown>,
      );

      baseConfig.params = formattedParams;
    }

    return baseConfig;
  }, [httpMethod, url, method, params]);

  /** return */
  return useMemo(
    () => ({
      key,
      config,
    }),
    [config, key],
  );
};
